import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaWifi, FaTv, FaSnowflake, FaShower, FaUtensils, FaCouch, FaConciergeBell
} from 'react-icons/fa';
import { MdKitchen, MdLocalLaundryService } from 'react-icons/md';
import { GiFridge, GiCookingPot, GiHotMeal } from 'react-icons/gi';
import { BiDish } from 'react-icons/bi';
import { TbHanger } from 'react-icons/tb';

const Amenities = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const amenitiesCategories = [
    {
      category: "Room Amenities",
      icon: <FaTv />,
      color: "bg-blue-500",
      items: [
        { icon: <FaWifi />, name: "High-speed WIFI" },
        { icon: <FaTv />, name: "TV & Fridge" },
        { icon: <FaSnowflake />, name: "Air Conditioner" },
        { icon: <FaShower />, name: "Hot Water" },
        { icon: <FaCouch />, name: "Common Sit-outs" },
        { icon: <TbHanger />, name: "Common Cloth Drying Unit" },
      ]
    },
    {
      category: "Dining Services",
      icon: <FaUtensils />,
      color: "bg-orange-500",
      items: [
        { icon: <GiHotMeal />, name: "Home-cooked meals (On Request)" },
        { icon: <GiCookingPot />, name: "Induction/stove" },
        { icon: <BiDish />, name: "Essential utensils" },
        { icon: <BiDish />, name: "Crockery" },
      ]
    }
  ];

  return (
    <section id="amenities" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="inline-block mb-4"
          >
            <FaConciergeBell className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Amenities & Facilities
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need for a comfortable and memorable stay
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {amenitiesCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: catIndex * 0.15 }}
              className="bg-gradient-to-br from-white to-primary-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
            >
              {/* Category Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className={`${category.color} text-white p-4 rounded-xl text-3xl`}>
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
              </div>

              {/* Amenities Grid */}
              <div className="grid grid-cols-2 gap-4">
                {category.items.map((item, itemIndex) => (
                  <motion.div
                    key={itemIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3 + catIndex * 0.1 + itemIndex * 0.05 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="flex items-center space-x-3 bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="text-primary-500 text-2xl group-hover:scale-125 transition-transform">
                      {item.icon}
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{item.name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary-600 to-nature-leaf text-white rounded-2xl p-8 md:p-12 relative overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
          
          <div className="relative z-10 text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">Need Something Special?</h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              We're happy to arrange special requests, custom meals, or any additional services 
              to make your stay even more memorable. Just let us know!
            </p>
            <a 
              href="#contact" 
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-full font-bold inline-block transition-all hover:scale-105 shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Amenities;
